/**
 * Due-date helpers — shared across Findings, CAP, Preview, and PDF.
 *
 * Postgres `date` values arrive as plain "YYYY-MM-DD" strings. Passing them
 * to `new Date(str)` parses as UTC midnight, which then renders as the
 * PREVIOUS day in any negative-UTC timezone (e.g. 2026-12-15 → "12/14/2026"
 * in US time zones). All date parsing for due dates must use this helper
 * so the calendar value the reviewer picked is the value displayed and
 * sorted by — no timezone drift.
 */

/** Parse "YYYY-MM-DD" (or full ISO) into a local-midnight Date. Returns null on invalid input. */
export function parseDueDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const s = String(value).trim();
  // YYYY-MM-DD (or YYYY-MM-DDTHH:...): use the date parts in LOCAL time.
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
    return dt;
  }
  // Fallback: let the browser try (e.g. "12/15/2026").
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt;
}

/** Format a due_date as MM/DD/YYYY (US). Returns "" when missing/invalid. */
export function formatDueDate(value: string | null | undefined): string {
  const d = parseDueDate(value);
  if (!d) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

/**
 * Sort key for due_date: epoch ms at local midnight, or +Infinity when missing.
 * Use with ascending compare so nulls always fall to the bottom.
 */
export function dueDateSortKey(value: string | null | undefined): number {
  const d = parseDueDate(value);
  return d ? d.getTime() : Number.POSITIVE_INFINITY;
}
